import { Box, Group, Stack, Tooltip } from '@mantine/core';
import { IconMenu, IconPencil, IconTrashX } from '@tabler/icons-react';
import { IAutomation } from 'pages/revisions-module/template-editor/editors/pipeline/types';
import AutomationPreview from 'pages/revisions-module/template-editor/editors/pipeline/AutomationPreview';
import { useAutomationConfigurator } from 'pages/revisions-module/template-editor/editors/pipeline/configurator/AutomationConfiguratorProvider';
import { usePipelineData } from 'pages/revisions-module/template-editor/editors/pipeline/PipelineDataProvider';
import { useConfirm } from 'components/modals/message/MessageProvider';
import P1Regular from 'components/typography/P1Regular';
import P2Medium from 'components/typography/P2Medium';
import { useMemo } from 'react';
import { AUTOMATION_TYPE_NAMES } from 'pages/revisions-module/template-editor/editors/pipeline/configurator/AutomationTypeSelect';
import { noop } from 'lodash';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';

interface AutomationRowProps {
  data: IAutomation;
  index: number;
  dragMode?: boolean;
  dropMode?: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}

/**
 * A single row in the automation editor.
 */
export default function AutomationRow({
  data,
  index,
  dragMode = false,
  dropMode = false,
  onDragStart = noop,
  onDragEnd = noop,
}: AutomationRowProps) {
  const { confirm } = useConfirm();
  const { configureAutomation } = useAutomationConfigurator();
  const { updateAutomation, removeAutomation } = usePipelineData();

  const automationTypeName = useMemo(() => AUTOMATION_TYPE_NAMES[data.type].label, [data.type]);

  /** Confirm removing the automation. */
  const confirmRemoveAutomation = () => {
    confirm({
      title: 'Odstrániť proces',
      content: 'Naozaj chcete odstrániť tento proces? Táto akcia je nevratná.',
      onConfirm: () => removeAutomation(data.id),
    });
  };

  /** Update the automation */
  const handleConfigureAutomation = () =>
    configureAutomation({
      initialValues: data,
      primaryButtonLabel: 'Upraviť proces',
      readOnly: { type: true },
      onSubmit: (automation) => updateAutomation(automation),
    });

  return (
    <Box
      p={16}
      style={{ borderBottom: '1px solid var(--mantine-color-gray-1)' }}
      sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 16, alignItems: 'start' }}
      opacity={dragMode ? 0.5 : 1}
      className="group/automation-row"
      data-index={index}
    >
      <Group noWrap spacing={16} align="flex-start" sx={{ minWidth: 0 }}>
        <Tooltip label="Presunúť proces" openDelay={300} withArrow={false} disabled={dropMode}>
          <ActionIcon
            variant="subtle-gray"
            size="sm"
            className="mt-[-4px] shrink-0 cursor-grab opacity-0 transition-opacity group-hover/automation-row:opacity-100"
            draggable
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          >
            <IconMenu />
          </ActionIcon>
        </Tooltip>

        <P1Regular c="gray.6" sx={{ flexShrink: 0 }}>
          #{index + 1}
        </P1Regular>

        <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
          <P2Medium>{automationTypeName}</P2Medium>
          <Box pl={16}>
            <AutomationPreview data={data} />
          </Box>
        </Stack>
      </Group>

      <Group noWrap>
        <Button variant="secondary" leftIcon={<IconPencil />} onClick={handleConfigureAutomation}>
          Upraviť
        </Button>
        <Tooltip label="Odstrániť proces" openDelay={300} withArrow={false} onClick={confirmRemoveAutomation}>
          <ActionIcon variant="subtle-gray" size="md">
            <IconTrashX />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Box>
  );
}
